import React, { FormEvent, useEffect, useState } from 'react';
import images from '../../constants/images';
import { useTranslation } from 'react-i18next';
import Header from '../../components/header/Header';
import '../../assets/styles/global.css';
import Map from '../../components/map/Map';
import styles from '../about/About.module.css';
import Footer from '../../components/footer/Footer';
import FaqSection from './FaqSection';
import CursorFollower from '../../components/cursor/Cursor';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import '../spinner.css';
import { useTheme, FormHelperText } from '@mui/material';

const ContactUs: React.FC = () => {
  const { t } = useTranslation('global');
  const [isLoading, setIsLoading] = useState(true);
  const [showButton, setShowButton] = useState(false);
  const [responseMsg, setResponseMsg] = useState('');
  const [responseMsgColor, setResponseMsgColor] = useState('success');
  const theme: any = useTheme();

  useEffect(() => {
    const hasLoadedBefore = sessionStorage.getItem('hasLoadedBefore');

    const timer = setTimeout(
      () => {
        setIsLoading(false);
        sessionStorage.setItem('hasLoadedBefore', 'true'); // mark as loaded
      },
      hasLoadedBefore ? 1000 : 3000,
    ); // shorter delay if already loaded

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowButton(window.scrollY > 410); // Show button after scrolling 200px
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleScrollToContact = (event: any) => {
    event.preventDefault(); // Prevent default anchor behavior
    const headerHeight = document.querySelector('header')?.offsetHeight || 0; // Get header height
    const targetPosition =
      document.getElementById('contact-section')?.offsetTop || 0;

    window.scrollTo({
      top: targetPosition - headerHeight, // Subtract the header height
      behavior: 'smooth', // Smooth scrolling effect
    });
  };
  const title = t('contact.contactTitle');
  const words = title.split(' ');

  const resetForm = () => {
    setFormData({
      from_name: '',
      email: '',
      mobile_number: '',
      subject: '',
      message: '',
    });

    setErrors({
      email: '',
    });
  };

  const [formData, setFormData] = useState({
    from_name: '',
    email: '',
    mobile_number: '',
    subject: '',
    message: '',
  });

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;

    setFormData({ ...formData, [name]: value });
  };

  const [errors, setErrors] = useState({
    email: '', // Only email will be validated
  });

  const validate = () => {
    const newErrors = { ...errors };
    let valid = true;

    // Email validation: Check if it's a valid email format
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailPattern.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
      valid = false;
    } else {
      newErrors.email = '';
    }

    setErrors(newErrors);
    return valid;
  };

  const sendEmail = (e: any) => {
    e.preventDefault();

    if (validate()) {
      const templateParams = {
        from_name: formData.from_name,
        email: formData.email,
        mobile_number: formData.mobile_number,
        subject: formData.subject,
        message: formData.message,
      };

      emailjs
        .send(
          process.env.REACT_APP_EMAILJS_SERVICE_ID ?? '', // Replace with your service ID
          process.env.REACT_APP_EMAILJS_INQUIRY_TEMPLATE_ID ?? '', // Replace with your template ID
          templateParams,
          {
            publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
          }, // Replace with your user ID from Email.js
        )
        .then(
          (response) => {
            console.log('Email sent successfully:', response);
            setResponseMsgColor('success');
            setResponseMsg(
              'Thank you for your inquiry! We will get back to you shortly.',
            );
            setTimeout(() => {
              setResponseMsg('');
            }, 3000);
            resetForm(); // <-- Clear the form here
          },
          (error) => {
            console.log('Email sending error:', error);
            setResponseMsgColor('error');
            setResponseMsg(
              'Oops! Something went wrong. Please try again later.',
            );
            setTimeout(() => {
              setResponseMsg('');
            }, 2000);
          },
        );
    } else {
      console.log('Validation failed!');
    }
  };

  if (isLoading) {
    return (
      <>
        <div className="loader-wrapper">
          <div className="spinner">
            <div className="cube1"></div>
            <div className="cube2"></div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <CursorFollower />

      {/* Hero Section */}
      <section
        id="hero"
        className="relative top-0 left-0 z-50 font flex flex-col justify-center items-center min-h-screen"
      >
        {/* Header */}
        <Header />

        {/* Background Image */}
        <img
          src={images.contact}
          alt="Contact Us Background"
          className="absolute inset-0 object-cover w-screen h-full"
        />

        {/* Overlay */}
        <div className="absolute inset-0"></div>

        {/* Content on top of the image */}
        <div
          className={`relative z-10 flex flex-col justify-between items-center sm:text-center text-left text-white px-4 md:px-48 lg:px-52 xl:px-64`}
        >
          {/* Responsive Heading */}
          <h1
            className={`title md:text-center text-4xl md:text-4xl lg:text-5xl xl:text-5xl 2xl:text-6xl font-bold pb-4 ${styles.head}`}
          >
            {words.map((word, index) => (
              <motion.span
                key={index}
                initial={{ opacity: 0, y: 50 }} // Start with invisible and slightly below
                animate={{ opacity: 1, y: 0 }} // Animate to fully visible and centered
                transition={{
                  delay: index * 0.2, // Delay each word animation
                  type: 'spring',
                  stiffness: 100,
                  damping: 25,
                }}
                className="inline-block mr-2" // Add some space between words
              >
                {word}
              </motion.span>
            ))}
          </h1>
        </div>

        {/* Scroll Button: Visible only on screens larger than 'lg' */}
        <div className="hidden lg:flex absolute lg:bottom-0 xl:bottom-0 flex-col items-center">
          <a
            href="#contact-section"
            onClick={handleScrollToContact}
            className="flex items-center justify-center color text-white  lg:w-[60px] lg:h-[60px] xl:w-[75px] xl:h-[75px] 2xl:w-[105px] 2xl:h-[105px]  hover:bg-red-500 transition duration-300"
          >
            {/* Icon and Text Container */}
            <div className="flex flex-col items-center">
              {/* Arrow Icon */}
              <img
                src={images.down}
                alt="Down Arrow"
                className="w-4 md:w-5 lg:w-5 xl:w-6 2xl:w-7 h-auto mb-1"
              />
              {/* Scroll Text */}
              <span className="text-xs md:text-sm lg:text-sm xl:text-base 2xl:text-lg">
                {t('heroSection.scroll')}
              </span>
            </div>
          </a>
        </div>

        {/* WhatsApp Floating Button */}
        <a
          href="https://wa.me/919909998890"
          target="_blank"
          rel="noopener noreferrer"
          className={`fixed bottom-10 right-5 sm:right-4 md:right-4 lg:right-8 xl:right-10 bg-green-500 text-white rounded-full p-2 lg:p-3 shadow-lg hover:bg-green-600 transition z-50 ${styles.whatsappBackground}`}
        >
          {/* WhatsApp Image Icon */}
          <img
            src={images.whatsapp_white}
            alt="WhatsApp Icon"
            className={`w-6 lg:w-7 h-auto ${styles.whatsappIcon}`}
          />
        </a>
      </section>

      {/* Contact with us */}
      <section className="relative flex flex-col" id="contact-section">
        <h2 className="title text-4xl pl-16 font-bold pt-10 text-black">
          {t('contact.contactWith')}
        </h2>
        <img
          src={images.rectangle}
          alt={'Rectangle'}
          className="h-12 md:h-14 lg:h-11 2xl:h-14 absolute left-4 top-28 md:left-14 lg:left-14 md:top-36 lg:top-44 bg-cover"
        />
        <div className="flex flex-col md:flex-row pb-14">
          <form
            onSubmit={sendEmail}
            className="relative justify-center space-y-4 lg:space-y-6 p-4 md:pl-16 md:pr-6 md:pt-8 md:w-2/3"
          >
            <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 space-x-0 md:space-x-4">
              <input
                type="text"
                name="from_name"
                value={formData.from_name}
                onChange={handleInputChange}
                placeholder="Name*"
                className="border p-3 w-full md:w-1/2 focus:outline-none focus:ring-1 focus:ring-[#797979]"
                required
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Email*"
                className={`border p-3 w-full md:w-1/2 focus:outline-none  ${errors.email ? 'border-red-500' : 'focus:ring-1 focus:ring-[#797979]'}`}
                required
              />
            </div>

            <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 space-x-0 md:space-x-4">
              <input
                type="number"
                name="mobile_number"
                value={formData.mobile_number}
                onChange={handleInputChange}
                placeholder="Phone"
                className="border p-3 w-full md:w-1/2 focus:outline-none focus:ring-1 focus:ring-[#797979]"
              />
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                placeholder="Subject"
                className="border p-3 w-full md:w-1/2 focus:outline-none focus:ring-1 focus:ring-[#797979]"
                required
              />
            </div>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Write Here*"
              className="border p-3 w-full focus:outline-none focus:ring-1 focus:ring-[#797979] h-44 resize"
              required
            ></textarea>

            <button
              type="submit"
              className="w-full md:w-32 color text-white py-3 font-semibold hover:bg-red-600 transition duration-300"
            >
              {t('contact.send')}
            </button>
            {responseMsg && (
              <FormHelperText
                sx={{
                  color:
                    responseMsgColor === 'success'
                      ? theme.palette.success.main
                      : theme.palette.error.main,
                  fontSize: '16px',
                  fontFamily: "'Nunito Sans', sans-serif",
                }}
              >
                {responseMsg}
              </FormHelperText>
            )}
          </form>
          <div className="md:w-1/2 md:pt-8 md:pl-0 pl-4 pt-5 pr-0 space-y-5">
            <a
              href="tel:+919909998890"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center font font-normal text-base hover:underline"
              style={{
                fontFamily: "'Nunito Sans', sans-serif",
                fontWeight: 400,
              }}
            >
              <img
                src={images.bgRedPhone}
                alt={'Phone'}
                className="pr-2 md:pr-4"
              />
              +91-9909998890
            </a>
            <a
              href="mailto:hello@elyumax.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center font font-normal text-base hover:underline"
              style={{
                fontFamily: "'Nunito Sans', sans-serif",
                fontWeight: 400,
              }}
            >
              <img
                src={images.bgRedEmail}
                alt={'Email'}
                className="pr-2 md:pr-4"
              />
              hello@elyumax.com
            </a>
            <a
              href="https://www.google.com/maps/place/Hetva+Enterprise/@21.4690196,73.0300072,17z/data=!3m1!4b1!4m6!3m5!1s0x3be03d0f4ac9ab79:0x3eecd961b30ca2c5!8m2!3d21.4690196!4d73.0325821!16s%2Fg%2F11k99s0v3p?entry=tts&g_ep=EgoyMDI1MDEyNy4wIPu8ASoASAFQAw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center font font-normal text-base w-80 hover:underline"
              style={{
                fontFamily: "'Nunito Sans', sans-serif",
                fontWeight: 400,
              }}
            >
              <img
                src={images.bgRedLocation}
                alt={'Location'}
                className="pr-2 md:pr-4"
              />
              Block No.110, Moti Pardi, Mangrol, Surat -394405
            </a>
            {/* Social Icons */}
            <div className="flex space-x-3 pt-10 md:pt-16">
              <a
                href="https://www.facebook.com/elyumax"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={images.facebook_gray}
                  alt="Facebook"
                  className={`w-6 md:w-8 hover:scale-125 transition-transform duration-300`}
                />
              </a>
              <a
                href="https://www.instagram.com/elyumax"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={images.instagram_gray}
                  alt="Instagram"
                  className={`w-6 md:w-8 hover:scale-125 transition-transform duration-300`}
                />
              </a>
              <a
                href="https://x.com/elyumax"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={images.twitter_gray}
                  alt="Twitter"
                  className={`w-6 md:w-8 hover:scale-125 transition-transform duration-300`}
                />
              </a>
              <a
                href="https://www.linkedin.com/company/elyumax"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={images.linkedin_gray}
                  alt="LinkedIn"
                  className={`w-6 md:w-8 hover:scale-125 transition-transform duration-300`}
                />
              </a>
            </div>
          </div>
        </div>
        <div className="md:flex hidden justify-center">
          <div className=" w-10 border-t border-black"></div>
        </div>

        {/* Scroll to Top Button */}
        {showButton && (
          <div className="fixed xl:top-1/3 lg:top-2/4 md:top-1/4 top-1/3 right-0 transform z-50 ">
            <div className="py-8 px-3 md:py-6 md:px-6 lg:py-7 xl:py-10 xl:px-16 "></div>
            <div
              className="absolute flex flex-col lg:-right-4 xl:right-0 translate-y-1/2 hidden lg:flex  "
              onClick={scrollToTop}
              style={{ cursor: 'pointer' }}
            >
              <img
                src={images.up}
                alt="UpArrow"
                className={`flex justify-center xl:pb-8 xl:h-16 lg:h-12 lg:pb-6 ${styles.icon}`}
              />
              <button
                onClick={scrollToTop}
                className={`flex text-sm md:text-lg lg:text-sm xl:text-base 2xl:text-lg text-red-500 rotate-90 transition duration-300 ${styles.top}`}
              >
                {t('whoWeAre.top')}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* FaqSection */}
      <div>
        <FaqSection />
      </div>

      {/* Map Section */}
      <Map />

      {/* Footer Section */}
      <Footer />
    </>
  );
};

export default ContactUs;
